import type { FC } from 'react';
import { matchPath, Navigate, useLocation } from 'react-router-dom';
import type { ButtonProps } from '@components/Button';
import Form from '@components/Form';
import Modal from '@components/Modal';
import { Stepper } from '@components/Stepper';
import { preventDefaultEnterSubmit } from '@helpers/preventDefaultEnterSubmit';
import { useUserTeams } from '@hooks/index';
import { isNonNullish } from '@root/helpers';
import Nav from '@src/routes';
import clsx from 'clsx';

import { formContainerClasses } from '../classes';
import { FormFooter } from '../components';
import { useCreateEntity, useCreateEntityForm, useStepper } from '../hooks';
import { Step } from '../types';
import { FormContent } from '../views';

interface CreateEntityModalProps {
  onClose: () => void;
}

export const CreateEntityModal: FC<CreateEntityModalProps> = ({ onClose }) => {
  const { pathname } = useLocation();

  const isTemplate = isNonNullish(matchPath(Nav.Templates, pathname));
  const isSubmission = isNonNullish(matchPath(Nav.Submissions, pathname));

  const type = isTemplate ? 'template' : 'contract';

  const { assignableTeams: userTeams, isFetching: isFetchingTeams } = useUserTeams();
  const { currentStep, getStepperItemState, onNext, onPrevious } = useStepper({ onCancel: onClose });
  const { isLoading, onSubmit } = useCreateEntity({ onClose, isTemplate });
  const { control, formValues, handleSubmit, isValid, setValue } = useCreateEntityForm(userTeams, currentStep);

  const commonButtonProps: ButtonProps = { isDisabled: !isValid, className: 'h-9.5', kind: 'primary' };

  const isLastStep = currentStep === Step.BrandingInfo;

  if (!isTemplate && !isSubmission) {
    return <Navigate to={Nav.NotFound} replace />;
  }

  return (
    <Modal
      className="w-lg gap-0"
      onClose={onClose}
      open
      shouldShowCloseIcon={false}
      title={`Create a new ${type}`}
      titleClassName="text-lg leading-6 font-medium justify-center!"
    >
      <div className={clsx(formContainerClasses)}>
        <p className="text-sm leading-5 font-normal text-gray-500">
          Please fill in the following information to setup a {type}
        </p>
        <div className="py-4">
          <Stepper getStepperItemState={getStepperItemState} />
        </div>
        <Form
          className="flex w-full flex-col gap-6"
          onSubmit={handleSubmit(onSubmit)}
          aria-label={`Create ${type} form`}
          onKeyDown={preventDefaultEnterSubmit}
        >
          <FormContent
            control={control}
            currentStep={currentStep}
            formValues={formValues}
            setValue={setValue}
            type={type}
            userTeams={userTeams}
            isFetchingTeams={isFetchingTeams}
          />

          <FormFooter
            {...commonButtonProps}
            currentStep={currentStep}
            isLastStep={isLastStep}
            isLoading={isLoading}
            onNext={onNext}
            onPrevious={onPrevious}
          />
        </Form>
      </div>
    </Modal>
  );
};
